/**
 * Job-scoped enrichment review saves that survive leaving the wizard.
 * In-flight createEntity / createRelationship work keeps updating the job
 * preview via PATCH even after EnrichReviewDialog unmounts.
 */

import { api } from "../services/api";
import {
  appendEnrichmentReviewAudit,
  shouldRecordEnrichmentAuditEvent,
} from "./enrichmentReviewAudit";
import { persistEnrichmentReviewNow } from "./debouncedEnrichmentReviewPersist";

/** @typedef {{ preview: object|null, inFlight: number, detached: boolean, ragIds: Set<string>, listeners: Set<Function> }} ReviewBackgroundSession */

/** @type {Map<string, ReviewBackgroundSession>} */
const sessions = new Map();

function sessionFor(jobId) {
  const jid = String(jobId || "").trim();
  if (!jid) return null;
  let s = sessions.get(jid);
  if (!s) {
    s = {
      preview: null,
      inFlight: 0,
      detached: false,
      ragIds: new Set(),
      listeners: new Set(),
    };
    sessions.set(jid, s);
  }
  return s;
}

function notify(jobId) {
  const s = sessions.get(String(jobId || "").trim());
  if (!s?.listeners?.size) return;
  const snapshot = {
    jobId: String(jobId || "").trim(),
    inFlight: s.inFlight,
    detached: s.detached,
    busy: s.inFlight > 0,
  };
  s.listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (e) {
      console.warn("enrichmentReviewBackground listener failed:", e);
    }
  });
}

/** Keep a durable preview snapshot for a job (call on every local patch). */
export function syncReviewBackgroundPreview(jobId, preview) {
  const s = sessionFor(jobId);
  if (!s || !preview) return;
  s.preview = preview;
}

export function getReviewBackgroundPreview(jobId) {
  return sessionFor(jobId)?.preview || null;
}

export function beginReviewBackgroundSave(jobId) {
  const s = sessionFor(jobId);
  if (!s) return;
  s.inFlight += 1;
  notify(jobId);
}

export function endReviewBackgroundSave(jobId) {
  const s = sessionFor(jobId);
  if (!s) return;
  s.inFlight = Math.max(0, s.inFlight - 1);
  notify(jobId);
  if (s.detached && s.inFlight === 0) {
    void finalizeDetachedSession(jobId);
  }
}

export function getReviewBackgroundInFlight(jobId) {
  const jid = String(jobId || "").trim();
  if (!jid) {
    let total = 0;
    sessions.forEach((s) => {
      total += s.inFlight;
    });
    return total;
  }
  return sessionFor(jid)?.inFlight || 0;
}

export function isReviewBackgroundDetached(jobId) {
  return Boolean(sessionFor(jobId)?.detached);
}

export function isReviewBackgroundBusy(jobId) {
  return getReviewBackgroundInFlight(jobId) > 0;
}

/**
 * Mark the session as detached so further patches persist without React.
 * Returns immediately — callers should close the UI without waiting.
 */
export function detachReviewBackground(jobId, preview = null) {
  const s = sessionFor(jobId);
  if (!s) return;
  if (preview) s.preview = preview;
  s.detached = true;
  notify(jobId);
  if (s.inFlight === 0) {
    void finalizeDetachedSession(jobId);
  }
}

/** Re-attach a session when the wizard reopens while background work continues. */
export function attachReviewBackground(jobId, preview = null) {
  const s = sessionFor(jobId);
  if (!s) return;
  s.detached = false;
  if (preview) s.preview = preview;
  notify(jobId);
}

export function trackReviewBackgroundRagIds(jobId, ...ids) {
  const s = sessionFor(jobId);
  if (!s) return;
  ids.forEach((id) => {
    const v = String(id || "").trim();
    if (v) s.ragIds.add(v);
  });
}

async function finalizeDetachedSession(jobId) {
  const jid = String(jobId || "").trim();
  const s = sessions.get(jid);
  if (!s || !s.detached || s.inFlight > 0) return;
  let snap = s.preview;
  const ragIds = [...s.ragIds];
  s.ragIds.clear();
  if (ragIds.length) {
    void api.regenerateRagSummariesBatch(ragIds, { background: true }).catch((e) => {
      console.warn("Background enrichment RAG refresh failed:", e);
    });
  }
  // All attended saves finished — mark the job review complete.
  if (snap && !snap?.enrichment_review_outcomes?.review_complete) {
    const ce = snap?.enrichment_review_outcomes?.catalog_entities || {};
    const pr = snap?.enrichment_review_outcomes?.proposed_relationships || {};
    const stillSaving =
      Object.values(ce).some((v) => v === "creating") ||
      Object.values(pr).some((v) => v === "approving" || v === "discarding");
    if (!stillSaving) {
      snap = {
        ...snap,
        enrichment_review_outcomes: {
          ...(snap.enrichment_review_outcomes || {}),
          review_complete: true,
        },
      };
      s.preview = snap;
    }
  }
  if (snap) {
    try {
      await persistEnrichmentReviewNow(jid, snap, null, { force: true });
    } catch (e) {
      console.warn("Background enrichment review persist failed:", e);
    }
  }
  const latest = sessions.get(jid);
  if (latest && latest.inFlight === 0 && latest.detached) {
    sessions.delete(jid);
  }
  notify(jobId);
}

/**
 * Apply a preview mutator against the durable snapshot and persist to the job.
 * Used when the wizard has detached (unmounted) so React state is gone.
 */
export function applyReviewBackgroundPatch(
  jobId,
  mutator,
  auditEvent = null,
  { persistImmediately = false } = {}
) {
  const jid = String(jobId || "").trim();
  const s = sessionFor(jid);
  if (!s || typeof mutator !== "function") return null;
  const base = s.preview;
  if (!base) return null;
  let next = mutator(base);
  const recordAudit = auditEvent && shouldRecordEnrichmentAuditEvent(auditEvent);
  if (recordAudit && next) {
    next = appendEnrichmentReviewAudit(next, auditEvent);
  }
  if (!next) return null;
  s.preview = next;
  void persistEnrichmentReviewNow(jid, next, recordAudit ? auditEvent : null).catch((e) => {
    console.warn("Background enrichment patch persist failed:", e);
  });
  notify(jid);
  return next;
}

export function subscribeReviewBackground(jobId, listener) {
  const s = sessionFor(jobId);
  if (!s || typeof listener !== "function") return () => {};
  s.listeners.add(listener);
  listener({
    jobId: String(jobId || "").trim(),
    inFlight: s.inFlight,
    detached: s.detached,
    busy: s.inFlight > 0,
  });
  return () => {
    s.listeners.delete(listener);
  };
}

export function waitForReviewBackgroundIdle(jobId, { maxMs = 120000 } = {}) {
  const jid = String(jobId || "").trim();
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (getReviewBackgroundInFlight(jid) === 0) {
        resolve();
        return;
      }
      if (Date.now() - started > maxMs) {
        resolve();
        return;
      }
      window.setTimeout(tick, 80);
    };
    tick();
  });
}
